"use client";

import Link from "next/link";
import { ArrowRight, BarChart3, Activity, Brain, TrendingUp, Layers, Globe, ChevronRight } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip,
  BarChart, Bar, Cell, CartesianGrid,
} from "recharts";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  analyticsStats, analyticsChildren, analyticsFeatures, analyticsUseCases,
  analyticsWhy, analyticsFaq, analyticsCta, sentimentTrend, engagementChannels,
} from "@/lib/data/analytics-hub";

/* ============================================================
   BESPOKE 1 — Analytics signature dashboard.
   Layered perception dashboard with sentiment timeline + perception index.
   ============================================================ */
function AnalyticsDashboardDiagram() {
  const ROUND = (n: number) => Math.round(n * 100) / 100;
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="aCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.85" />
            <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#dc143c" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="aBar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <circle cx="260" cy="260" r="200" fill="url(#aCore)" />
        {/* Concentric rings */}
        {[100, 145, 195].map((r, i) => (
          <circle key={i} cx="260" cy="260" r={r} fill="none" stroke="rgb(255 255 255 / 0.07)" strokeWidth="0.6" strokeDasharray={i % 2 === 0 ? "1 3" : "2 6"} />
        ))}
        {/* 144-axis spoke grid */}
        {Array.from({ length: 36 }).map((_, i) => {
          const angle = (i / 36) * Math.PI * 2;
          const x = ROUND(260 + Math.cos(angle) * 195);
          const y = ROUND(260 + Math.sin(angle) * 195);
          return <line key={`s-${i}`} x1="260" y1="260" x2={x} y2={y} stroke="rgb(255 255 255 / 0.04)" strokeWidth="0.3" />;
        })}
        {/* Bar chart silhouette inside */}
        {Array.from({ length: 16 }).map((_, i) => {
          const h = 30 + Math.abs(Math.sin(i * 0.7)) * 80 + (i % 3) * 14;
          const x = 100 + i * 20;
          return <rect key={`b-${i}`} x={x} y={330 - h} width="12" height={h} fill="url(#aBar)" opacity="0.65" rx="2" />;
        })}
        {/* 144 dimension tick marks */}
        {Array.from({ length: 144 }).map((_, i) => {
          const angle = (i / 144) * Math.PI * 2 - Math.PI / 2;
          const r1 = 200;
          const r2 = 207;
          const x1 = ROUND(260 + Math.cos(angle) * r1);
          const y1 = ROUND(260 + Math.sin(angle) * r1);
          const x2 = ROUND(260 + Math.cos(angle) * r2);
          const y2 = ROUND(260 + Math.sin(angle) * r2);
          return <line key={`t-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgb(6 182 212 / 0.4)" strokeWidth="0.4" />;
        })}
        {/* Center label */}
        <g transform="translate(260, 260)">
          <circle r="60" fill="rgb(10 10 18 / 0.92)" />
          <circle r="50" fill="none" stroke="rgb(6 182 212 / 0.5)" strokeWidth="1" />
          <text x="0" y="-4" textAnchor="middle" fontSize="20" fontWeight="900" fill="white" letterSpacing="-0.5">
            144D
          </text>
          <text x="0" y="12" textAnchor="middle" fontSize="6" fontWeight="700" fill="#06b6d4" letterSpacing="2">
            ANALYTICS
          </text>
          <text x="0" y="22" textAnchor="middle" fontSize="4.5" fill="rgb(255 255 255 / 0.5)" letterSpacing="1.5">
            8 SURFACES
          </text>
        </g>
      </svg>
      <div className="absolute top-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <BarChart3 className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">8 Surfaces</span>
      </div>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Brain className="h-3.5 w-3.5" style={{ color: "var(--color-purple)" }} />
        <span className="text-xs font-semibold">144 Dimensions</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "2.5s" }}>
        <Activity className="h-3.5 w-3.5" style={{ color: "var(--color-crimson)" }} />
        <span className="text-xs font-semibold">&lt;1s Refresh</span>
      </div>
      <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "3.5s" }}>
        <Globe className="h-3.5 w-3.5" style={{ color: "var(--color-emerald)" }} />
        <span className="text-xs font-semibold">50+ Platforms</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Sentiment trend area chart.
   Positive / neutral / negative stacked + perception index overlay.
   ============================================================ */
function SentimentTrendChart() {
  return (
    <div className="relative w-full h-[380px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={sentimentTrend} margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
          <defs>
            <linearGradient id="aPos" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="aNeu" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.7} />
              <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="aNeg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#dc143c" stopOpacity={0.7} />
              <stop offset="100%" stopColor="#dc143c" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgb(255 255 255 / 0.05)" />
          <XAxis dataKey="day" tick={{ fontSize: 11, fill: "rgb(255 255 255 / 0.6)" }} />
          <YAxis tick={{ fontSize: 11, fill: "rgb(255 255 255 / 0.5)" }} />
          <Tooltip
            contentStyle={{
              background: "rgb(10 10 18 / 0.95)",
              border: "1px solid rgb(255 255 255 / 0.1)",
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: "rgb(255 255 255 / 0.7)" }}
          />
          <Area type="monotone" dataKey="positive" stackId="1" stroke="#10b981" strokeWidth={2} fill="url(#aPos)" name="Positive %" />
          <Area type="monotone" dataKey="neutral" stackId="1" stroke="#06b6d4" strokeWidth={2} fill="url(#aNeu)" name="Neutral %" />
          <Area type="monotone" dataKey="negative" stackId="1" stroke="#dc143c" strokeWidth={2} fill="url(#aNeg)" name="Negative %" />
          <Area type="monotone" dataKey="perception" stroke="#fbbf24" strokeWidth={3} fill="none" strokeDasharray="4 4" name="Perception Index" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Engagement channel breakdown horizontal bars.
   Volume / resonance / conversion per channel.
   ============================================================ */
function EngagementChannelBreakdown() {
  return (
    <div className="space-y-4">
      {engagementChannels.map((c, i) => (
        <Reveal key={c.channel} delay={i * 60}>
          <div className="glass rounded-xl p-4" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${c.accent}) 12%, transparent)` }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">{c.channel}</span>
              <span className="eyebrow-tiny font-bold" style={{ color: `var(--color-${c.accent})` }}>
                RES {c.resonance} · CVR {c.conversion}%
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold mb-1">Volume</div>
                <div className="h-2 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${c.volume}%`, background: `var(--color-${c.accent})` }} />
                </div>
              </div>
              <div>
                <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold mb-1">Resonance</div>
                <div className="h-2 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${c.resonance}%`, background: `var(--color-${c.accent})`, opacity: 0.7 }} />
                </div>
              </div>
              <div>
                <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold mb-1">Conversion</div>
                <div className="h-2 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${c.conversion * 3}%`, background: `var(--color-${c.accent})`, opacity: 0.85 }} />
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function AnalyticsHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="crimson"
      featuresWrap="purple"
      useCasesWrap="emerald"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-purple)] opacity-22 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-20 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">Perception X2 Analytics</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    Eight Analytics{" "}
                    <span className="bg-gradient-to-br from-[var(--color-purple)] via-[var(--color-cyan)] to-[var(--color-emerald)] bg-clip-text text-transparent">
                      Surfaces. One Data Fabric.
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    144-dimensional perception telemetry at sub-second refresh across 50+ platforms. Dashboards, predictions, and reports that always agree.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    Real-time dashboards, performance metrics, engagement telemetry, comparative analytics, predictive forecasting, sentiment analysis, conversion attribution, and custom reports — all reading from the same perception data fabric.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/analytics/dashboards"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-purple)/30]"
                    >
                      Open Live Dashboards <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/predictive-analytics"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      Predictive Analytics
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {analyticsStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <AnalyticsDashboardDiagram />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <HubOverview
          eyebrow="Eight Analytics Surfaces"
          title="Every Dimension of Perception Intelligence"
          description="Perception X2 organizes analytics into eight purpose-built surfaces — each addressing a distinct analytical posture, all reading from the same data fabric."
          items={analyticsChildren}
          accent="crimson"
        />
      }
      features={
        <>
          <Section id="sentiment" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Sentiment Trend"
                title="144-Dimensional Sentiment Over Time"
                description="A two-week sentiment timeline from a representative engagement — positive, neutral, and negative share stacked, with the Perception X2 Index overlaid. Notice the inflection point at deployment and the compounding curve thereafter."
                accent="cyan"
              />
              <div className="glass rounded-2xl p-6">
                <SentimentTrendChart />
              </div>
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="Analytics Capabilities"
                title="The Capabilities That Compose the Stack"
                description="Eight foundational capabilities shared across the Perception X2 analytics platform — each engineered for machine-speed insight and audit-grade lineage."
                accent="purple"
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {analyticsFeatures.map((f, i) => (
                  <Reveal key={f.title} delay={i * 50}>
                    <div
                      className="glass rounded-2xl p-5 h-full card-glow"
                      style={{ ["--glow" as string]: `var(--color-${f.accent})` }}
                    >
                      <div
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold">{f.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Analytics Applications"
          title="Six Operational Patterns"
          description="The Perception X2 analytics stack serves six primary operational patterns — each tuned to a distinct strategic posture."
          cases={analyticsUseCases}
          accent="emerald"
        />
      }
      why={
        <>
          <Section id="channels" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Channel Breakdown"
                title="Engagement Across Every Digital Surface"
                description="Volume, resonance, and conversion across the eight primary engagement channels. The breakdown reveals where Perception X2 investments yield the strongest returns for any given engagement profile."
                accent="cyan"
              />
              <EngagementChannelBreakdown />
            </Container>
          </Section>
          <Section id="why" className="relative mt-8">
            <Container>
              <HubWhy
                eyebrow="Why These Analytics"
                title="The Differentiators That Compound"
                items={analyticsWhy}
                accent="cyan"
              />
            </Container>
          </Section>
        </>
      }
      faq={
        <HubFAQ
          eyebrow="Analytics FAQ"
          title="Frequently Asked Questions"
          items={analyticsFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={analyticsCta.eyebrow}
          title={analyticsCta.title}
          description={analyticsCta.description}
          primary={analyticsCta.primary}
          secondary={analyticsCta.secondary}
          trustItems={analyticsCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
